import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { allGalleryProjects, galleryCategories } from '../../mocks/galleryData';

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const modalContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const filteredProjects = activeCategory === 'all'
    ? allGalleryProjects
    : allGalleryProjects.filter((p) => p.category === activeCategory);

  const selected = allGalleryProjects.find((p) => p.id === selectedProject);

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    setSliderPosition(Math.max(3, Math.min(97, x)));
  };

  const closeModal = () => {
    setSelectedProject(null);
    setSliderPosition(50);
  };

  const getProjectThumbnail = (project: typeof allGalleryProjects[0]) => {
    if (project.galleryImages && project.galleryImages.length > 0) {
      return project.galleryImages[0];
    }
    if (project.afterImages && project.afterImages.length > 0) {
      return project.afterImages[0];
    }
    return project.afterImage;
  };

  const hasGalleryImages = (project: typeof allGalleryProjects[0]) => {
    return project.galleryImages && project.galleryImages.length > 0;
  };

  const hasBeforeAfterImages = (project: typeof allGalleryProjects[0]) => {
    return project.beforeImages && project.beforeImages.length > 0 && project.afterImages && project.afterImages.length > 0;
  };

  const isSlider = (project: typeof allGalleryProjects[0]) => {
    return !hasBeforeAfterImages(project) && project.beforeImage !== project.afterImage;
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14 md:h-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-charcoal hover:text-teal transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              <span className="font-body text-sm">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <img
                src="https://static.readdy.ai/image/0e9775581447daa16a16441c9f113381/b806c298fd480fe9b398164c0551d937.png"
                alt="Aria Property Improvement"
                className="h-8 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </nav>

      <section className="bg-charcoal pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto text-center">
            <span className="inline-block font-body font-semibold text-teal text-xs uppercase tracking-widest mb-3 bg-white/10 px-3 py-1 rounded-full">
              Our Portfolio
            </span>
            <h1 className="font-heading font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-3">
              Every Project Tells a Story
            </h1>
            <p className="font-body text-white/60 text-sm md:text-base max-w-xl mx-auto">
              Browse our complete collection of before &amp; after transformations across South Florida.
              From kitchens and bathrooms to full rebuilds — we do it all, and we do it right.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-concrete-light py-6 md:py-8 sticky top-16 md:top-20 z-30">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {galleryCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-5 py-2 rounded-full font-body text-sm transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-teal text-white'
                      : 'bg-white text-charcoal hover:bg-white/80'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-10 md:py-14">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => {
                    setSelectedProject(project.id);
                    setSliderPosition(50);
                  }}
                  className="group bg-white rounded-lg overflow-hidden border border-concrete hover:border-teal/40 transition-all text-left cursor-pointer"
                >
                  <div className="relative h-[280px] overflow-hidden bg-concrete-light flex items-center justify-center">
                    <img
                      src={getProjectThumbnail(project)}
                      alt={project.title}
                      className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-charcoal font-body text-xs px-4 py-2 rounded-full shadow-lg">
                        View Project
                      </span>
                    </div>
                    <div className="absolute top-3 left-3">
                      <span className="bg-white/90 text-charcoal font-body text-xs px-2.5 py-1 rounded-full">
                        {project.type}
                      </span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-charcoal text-sm md:text-base mb-2">{project.title}</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-body text-charcoal-light text-xs flex items-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-map-pin-line text-teal text-xs"></i>
                        </div>
                        {project.location}
                      </span>
                      <span className="font-body text-charcoal-light text-xs flex items-center gap-1">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-time-line text-teal text-xs"></i>
                        </div>
                        {project.duration}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-concrete-light mb-4">
                  <i className="ri-image-line text-charcoal-light text-2xl"></i>
                </div>
                <p className="font-body text-charcoal-light text-sm">No projects in this category yet.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/80" onClick={closeModal}>
          <div
            className="relative w-full max-w-5xl bg-white rounded-lg overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-charcoal/50 flex items-center justify-center hover:bg-charcoal/70 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>

            {hasBeforeAfterImages(selected) ? (
              <div className="w-full bg-concrete-light">
                {/* BEFORE Section */}
                <div className="border-b border-gray-200">
                  <div className="bg-charcoal px-4 md:px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-arrow-left-line text-white text-sm"></i>
                      </div>
                      <span className="font-heading font-bold text-white text-sm uppercase tracking-wider">Before</span>
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {selected.beforeImages!.map((img, idx) => (
                        <div key={idx} className="relative flex items-center justify-center bg-white rounded-lg overflow-hidden">
                          <img
                            src={img}
                            alt={`${selected.title} Before ${idx + 1}`}
                            className="max-w-full max-h-[60vh] object-contain"
                            draggable={false}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* AFTER Section */}
                <div>
                  <div className="bg-teal px-4 md:px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-white text-sm"></i>
                      </div>
                      <span className="font-heading font-bold text-white text-sm uppercase tracking-wider">After</span>
                    </div>
                  </div>
                  <div className="p-4 md:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {selected.afterImages!.map((img, idx) => (
                        <div key={idx} className="relative flex items-center justify-center bg-white rounded-lg overflow-hidden">
                          <img
                            src={img}
                            alt={`${selected.title} After ${idx + 1}`}
                            className="max-w-full max-h-[60vh] object-contain"
                            draggable={false}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : isSlider(selected) ? (
              <div
                ref={modalContainerRef}
                className="relative w-full h-[400px] md:h-[500px] cursor-ew-resize select-none"
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                onTouchStart={() => setIsDragging(true)}
                onTouchEnd={() => setIsDragging(false)}
                onTouchMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.touches[0].clientX - rect.left) / rect.width) * 100;
                  setSliderPosition(Math.max(3, Math.min(97, x)));
                }}
              >
                {/* After image */}
                <img
                  src={selected.afterImage}
                  alt={`${selected.title} after`}
                  className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
                  draggable={false}
                />
                <div className="absolute top-4 right-4 bg-charcoal/60 px-3 py-1 rounded text-white font-body text-xs uppercase tracking-wider z-10">
                  After
                </div>

                {/* Before image */}
                <div
                  className="absolute inset-y-0 left-0 overflow-hidden"
                  style={{ width: `${sliderPosition}%` }}
                >
                  <img
                    src={selected.beforeImage}
                    alt={`${selected.title} before`}
                    className="absolute inset-0 h-full object-cover object-top pointer-events-none"
                    style={{ width: modalContainerRef.current ? `${modalContainerRef.current.offsetWidth}px` : '100%', maxWidth: 'none' }}
                    draggable={false}
                  />
                  <div className="absolute top-4 left-4 bg-black/60 px-3 py-1 rounded text-white font-body text-xs uppercase tracking-wider">
                    Before
                  </div>
                </div>

                {/* Slider handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-teal cursor-ew-resize z-20"
                  style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                >
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-teal flex items-center justify-center shadow-lg">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-left-right-line text-white text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>
            ) : hasGalleryImages(selected) ? (
              <div className="w-full bg-concrete-light">
                <div className="bg-teal px-4 md:px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-image-line text-white text-sm"></i>
                    </div>
                    <span className="font-heading font-bold text-white text-sm uppercase tracking-wider">Project Gallery</span>
                  </div>
                </div>
                <div className="p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                    {selected.galleryImages!.map((img, idx) => (
                      <div key={idx} className="relative flex items-center justify-center bg-white rounded-lg overflow-hidden">
                        <img
                          src={img}
                          alt={`${selected.title} ${idx + 1}`}
                          className="max-w-full max-h-[60vh] object-contain"
                          draggable={false}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative w-full max-h-[70vh] flex items-center justify-center bg-concrete-light">
                <img
                  src={selected.afterImage}
                  alt={selected.title}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            )}

            <div className="p-5 md:p-6">
              <h3 className="font-heading font-bold text-charcoal text-lg md:text-xl mb-1">{selected.title}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-2 mb-3">
                <span className="font-body text-charcoal-light text-sm flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-map-pin-line text-teal text-sm"></i>
                  </div>
                  {selected.location}
                </span>
                <span className="font-body text-charcoal-light text-sm flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line text-teal text-sm"></i>
                  </div>
                  {selected.duration}
                </span>
                <span className="bg-concrete-light text-charcoal font-body text-xs px-2.5 py-1 rounded-full">{selected.type}</span>
              </div>
              <p className="font-body text-charcoal-light text-sm leading-relaxed">{selected.description}</p>
              {selected.review && (
                <div className="mt-4 border-t border-concrete pt-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map((s) => (
                          <div key={s} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-teal text-xs"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-body text-charcoal text-sm italic leading-relaxed">&ldquo;{selected.review.text}&rdquo;</p>
                      <p className="font-body text-charcoal-light text-xs mt-1.5 font-semibold">&mdash; {selected.review.author}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}